import { AnalyticsField, AnalyticsFieldFormRow } from '../interfaces/Agent';
import { TranslationKey } from '../types/i18n';

// Same limits as the backend check on save.
export const MAX_ANALYTICS_FIELDS = 30;
export const MAX_FIELD_NAME_LENGTH = 60;
export const MAX_FIELD_DESCRIPTION_LENGTH = 500;
export const MAX_FIELD_OPTIONS = 20;
export const MIN_FIELD_OPTIONS = 2;
export const MAX_FIELD_OPTION_LENGTH = 60;
export const MAX_TOTAL_FIELD_OPTIONS = 100;
export const MAX_SUMMARY_INSTRUCTION_LENGTH = 2000;

export const emptyAnalyticsFieldRow = (): AnalyticsFieldFormRow => ({
  name: '',
  description: '',
  kind: 'text',
  options: [],
  range_low: '',
  range_high: '',
});

export const toAnalyticsFieldRows = (
  fields: AnalyticsField[] | undefined
): AnalyticsFieldFormRow[] =>
  (fields ?? []).map(field => ({
    name: field.name,
    description: field.description,
    kind: field.kind,
    options: field.options ?? [],
    range_low: field.range ? String(field.range[0]) : '',
    range_high: field.range ? String(field.range[1]) : '',
  }));

export const isBlankRow = (row: AnalyticsFieldFormRow) =>
  !row.name?.trim() &&
  !row.description?.trim() &&
  !(
    row.kind === 'choice' && (row.options ?? []).some(option => option.trim())
  ) &&
  !(row.kind === 'number' && (row.range_low?.trim() || row.range_high?.trim()));

export const cleanOptions = (row: AnalyticsFieldFormRow): string[] =>
  (row.options ?? []).map(option => option.trim()).filter(Boolean);

export const hasRepeatedOptions = (row: AnalyticsFieldFormRow): boolean => {
  const options = cleanOptions(row).map(option => option.toLowerCase());
  return new Set(options).size !== options.length;
};

export const totalOptions = (rows: AnalyticsFieldFormRow[]): number =>
  rows
    .filter(row => row.kind === 'choice')
    .reduce((total, row) => total + cleanOptions(row).length, 0);

const fieldKey = (row: AnalyticsFieldFormRow | undefined) =>
  (row?.name ?? '').trim().toLowerCase();

export const isNameUsedTwice = (
  rows: AnalyticsFieldFormRow[],
  index: number
): boolean => {
  const name = fieldKey(rows[index]);
  return (
    !!name &&
    rows.some((row, other) => other !== index && fieldKey(row) === name)
  );
};

const toNumber = (value: string | undefined): number | undefined => {
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// A number field's range is either left empty or set from low to high.
export const isRangeValid = (
  low: string | undefined,
  high: string | undefined
): boolean => {
  if (!low?.trim() && !high?.trim()) return true;
  const lowNumber = toNumber(low);
  const highNumber = toNumber(high);
  return (
    lowNumber !== undefined &&
    highNumber !== undefined &&
    lowNumber < highNumber
  );
};

export const isAnalyticsFieldRowComplete = (
  row: AnalyticsFieldFormRow
): boolean => {
  if (!row.name?.trim() || !row.description?.trim()) return false;
  if (row.kind === 'choice') {
    return (
      cleanOptions(row).length >= MIN_FIELD_OPTIONS && !hasRepeatedOptions(row)
    );
  }
  if (row.kind === 'number') return isRangeValid(row.range_low, row.range_high);
  return true;
};

// Rows left fully empty are dropped on save, like empty category rows.
export const isAnalyticsFieldsComplete = (
  rows: AnalyticsFieldFormRow[] | undefined
): boolean => {
  if (!rows) return true;
  return (
    rows.every(row => isBlankRow(row) || isAnalyticsFieldRowComplete(row)) &&
    !rows.some((_, index) => isNameUsedTwice(rows, index)) &&
    totalOptions(rows) <= MAX_TOTAL_FIELD_OPTIONS
  );
};

export const toAnalyticsFieldsPayload = (
  rows: AnalyticsFieldFormRow[] | undefined
): AnalyticsField[] =>
  (rows ?? [])
    .filter(row => !isBlankRow(row))
    .map(row => {
      const field: AnalyticsField = {
        name: row.name.trim(),
        description: row.description.trim(),
        kind: row.kind,
      };
      if (row.kind === 'choice') field.options = cleanOptions(row);
      const low = toNumber(row.range_low);
      const high = toNumber(row.range_high);
      if (row.kind === 'number' && low !== undefined && high !== undefined) {
        field.range = [low, high];
      }
      return field;
    });

type Translate = (key: TranslationKey) => string;

// How one extracted answer reads on screen. Not said in the call shows as --.
export const formatExtractedValue = (
  value: string | number | boolean | null | undefined,
  t: Translate
): string => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  if (typeof value === 'number') {
    return new Intl.NumberFormat('lb-LU').format(value);
  }
  return String(value);
};

// Same as on screen, except numbers stay plain and empty stays empty.
export const formatExtractedValueForCsv = (
  value: string | number | boolean | null | undefined,
  t: Translate
): string | number => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  return value;
};
